import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || "https://dataleo.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;

/**
 * SeoHead - unified <head> manager for SEO + GEO.
 *
 * Renders title, meta description, canonical, OpenGraph, Twitter Card,
 * hreflang alternates and JSON-LD structured data in one place.
 *
 * Props:
 *  - title: page title (raw, "| Dataleo Supply Chain AI Radar" appended unless `bareTitle`)
 *  - description: 120-160 char meta description
 *  - path: pathname for the canonical URL (e.g. "/news/abc")
 *  - lang: "en" | "fr"
 *  - image: absolute or relative URL for og:image / twitter:image
 *  - type: og:type ("website" | "article")
 *  - jsonLd: object or array of objects for JSON-LD schema(s)
 *  - alternates: { en: "/news/x-en", fr: "/news/x-fr" } for hreflang
 *  - noindex: boolean - emit <meta name="robots" content="noindex">
 */
const SeoHead = ({
  title,
  description,
  path = "/",
  lang = "en",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  alternates,
  bareTitle = false,
  noindex = false,
  publishedTime,
  modifiedTime,
  canonicalPath,
}) => {
  const isRadarPage = path === "/radar" || path.startsWith("/radar/") || ["/news", "/jobs", "/alerts", "/insights", "/search", "/topic", "/tech", "/ecosystem"].some((p) => path === p || path.startsWith(`${p}/`));
  const fullTitle = bareTitle ? title : `${title} | ${isRadarPage ? "Dataleo Radar" : "Dataleo"}`;
  const previewHost = typeof window !== "undefined" && (/\.preview\.emergentagent\.com$/i.test(window.location.hostname) || window.location.hostname.endsWith(".vercel.app"));
  const shouldNoindex = noindex || previewHost;
  // Build language-specific canonical: EN = bare path, FR = `?lang=fr`.
  // This matches the hreflang convention emitted below so each language
  // variant has a distinct canonical URL (avoiding the "same URL, two
  // contents" SEO smell on a localStorage-driven SPA).
  const cleanPath = path.split("?")[0];
  const langCanonicalPath = lang === "fr" ? `${cleanPath}?lang=fr` : cleanPath;
  // Single-language records (articles) pass `canonicalPath`: no ?lang suffix.
  const canonical = `${SITE_URL}${canonicalPath || langCanonicalPath}`;
  const imgAbs = image && image.startsWith("http") ? image : `${SITE_URL}${image || ""}`;
  const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : jsonLd ? [jsonLd] : [];

  // Explicit alternates are emitted as-is (an untranslated article only lists
  // itself); otherwise default reciprocal EN / ?lang=fr alternates.
  const alts = alternates
    ? [["en", alternates.en], ["fr", alternates.fr], ["x-default", alternates.xDefault ?? alternates.en]]
    : [["en", cleanPath], ["fr", `${cleanPath}?lang=fr`], ["x-default", cleanPath]];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {shouldNoindex
        ? <meta name="robots" content="noindex,follow" />
        : <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />}

      {/* hreflang alternates - reciprocal en/fr + x-default */}
      {alts.filter(([, href]) => href).map(([hl, href]) => (
        <link key={hl} rel="alternate" hrefLang={hl} href={`${SITE_URL}${href}`} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={isRadarPage ? "Dataleo Radar" : "Dataleo"} />
      <meta property="og:locale" content={lang === "fr" ? "fr_FR" : "en_US"} />
      {imgAbs && <meta property="og:image" content={imgAbs} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imgAbs && <meta name="twitter:image" content={imgAbs} />}

      {/* JSON-LD structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
export { SITE_URL };
